// The signal that drives the blob. One AudioContext for the whole page, one
// envelope follower shared by the microphone and the consultant's voice.
//
// Constants below were measured, not guessed (.research/code/blob-webgl).
// Changing them changes how the blob *feels*, so change them deliberately.

export interface LevelOptions {
  fftSize: number;
  /** AnalyserNode.smoothingTimeConstant. */
  smoothing: number;
  /** dBFS window mapped onto 0..1. */
  floorDb: number;
  ceilDb: number;
  /** Envelope follower time constants, in seconds. */
  attack: number;
  release: number;
}

// Smoothing sits below the 0.8 spec default on purpose: the analyser's own IIR
// otherwise compounds with the follower below, and attack and release stop
// being independently controllable. Keep the analyser fast, shape it here.
// The mic wants to feel instant; a spoken reply wants to feel like breath.
export const MIC_LEVEL_OPTIONS: LevelOptions = {
  fftSize: 1024,
  smoothing: 0.55,
  floorDb: -62,
  ceilDb: -16,
  attack: 0.02,
  release: 0.19,
};

export const PLAYBACK_LEVEL_OPTIONS: LevelOptions = {
  fftSize: 2048,
  smoothing: 0.72,
  floorDb: -52,
  ceilDb: -10,
  attack: 0.032,
  release: 0.26,
};

/** Frames can be arbitrarily far apart after a hidden tab wakes up. */
const MAX_FRAME_SECONDS = 0.1;

let shared: AudioContext | null = null;
let unavailable = false;

/**
 * Returns the page's AudioContext, creating and resuming it as needed. Must be
 * reached from a user gesture the first time: browsers start the context
 * suspended, and a suspended context means the consultant speaks silently once
 * an element has been routed through the graph.
 *
 * Resolves to null when Web Audio is unavailable or still blocked — every
 * caller must stay usable without a level signal rather than fail.
 */
export async function unlockAudio(): Promise<AudioContext | null> {
  if (unavailable) return null;
  try {
    if (!shared) shared = new AudioContext({ latencyHint: "interactive" });
    if (shared.state === "suspended") await shared.resume();
    return shared;
  } catch {
    // Either the constructor is missing or resume() was called outside a
    // gesture. Both are recoverable on the next attempt, except a missing
    // constructor, which we latch so we stop retrying every frame.
    if (!shared) unavailable = true;
    return shared;
  }
}

/** The context if one already exists and is running; never creates one. */
export function runningAudioContext(): AudioContext | null {
  return shared && shared.state === "running" ? shared : null;
}

function follow(prev: number, target: number, dt: number, attack: number, release: number): number {
  const tau = target > prev ? attack : release;
  return prev + (target - prev) * (1 - Math.exp(-dt / Math.max(tau, 1e-4)));
}

/**
 * Reads loudness off an AnalyserNode and smooths it into the 0..1 value the
 * blob renders. Drives itself on requestAnimationFrame so callers only read
 * `level`.
 */
export class LevelMeter {
  private readonly analyser: AnalyserNode;
  // Explicit buffer type: getFloatTimeDomainData rejects a SharedArrayBuffer view.
  private readonly samples: Float32Array<ArrayBuffer>;
  private readonly options: LevelOptions;
  private current = 0;
  private highest = 0;
  private frame = 0;
  private lastFrameMs = 0;
  // Tracks room tone so the blob does not breathe with the air conditioning.
  private noiseFloorDb = -70;

  constructor(ctx: AudioContext, source: AudioNode, options: LevelOptions) {
    this.options = options;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = options.fftSize;
    analyser.smoothingTimeConstant = options.smoothing;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;
    source.connect(analyser);
    this.analyser = analyser;
    this.samples = new Float32Array(analyser.fftSize);
  }

  /** 0..1, smoothed. */
  get level(): number {
    return this.current;
  }

  /** Highest level seen since the last reset — the silence guard reads this. */
  get peak(): number {
    return this.highest;
  }

  start(): void {
    if (this.frame) return;
    this.lastFrameMs = performance.now();
    const step = (now: number) => {
      this.frame = requestAnimationFrame(step);
      const dt = Math.min((now - this.lastFrameMs) / 1000, MAX_FRAME_SECONDS);
      this.lastFrameMs = now;
      if (dt > 0) this.tick(dt);
    };
    this.frame = requestAnimationFrame(step);
  }

  stop(): void {
    if (!this.frame) return;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /** Drops the level to zero so the blob stops moving on the same frame. */
  reset(): void {
    this.current = 0;
    this.highest = 0;
    this.noiseFloorDb = -70;
  }

  dispose(): void {
    this.stop();
    this.analyser.disconnect();
  }

  private tick(dt: number): void {
    // RMS over the TIME domain. An average of frequency bins tracks spectral
    // shape rather than loudness, which makes sibilants read as loud as vowels
    // and the blob chatter through every "s".
    this.analyser.getFloatTimeDomainData(this.samples);
    let sum = 0;
    for (let i = 0; i < this.samples.length; i++) {
      const sample = this.samples[i] ?? 0;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / this.samples.length);
    const db = 20 * Math.log10(Math.max(rms, 1e-7));

    // Rises at 2 dB/s toward the current level and never sits above it, so a
    // quiet room is learned within a second but a loud one is left alone.
    this.noiseFloorDb = Math.min(this.noiseFloorDb + dt * 2, Math.max(db, -90));
    const floor = Math.max(this.noiseFloorDb + 6, this.options.floorDb);

    let target = (db - floor) / (this.options.ceilDb - floor);
    target = Math.max(0, Math.min(1, target));
    target = Math.pow(target, 0.72); // gentle expansion: quiet speech still moves it

    this.current = follow(this.current, target, dt, this.options.attack, this.options.release);
    if (this.current > this.highest) this.highest = this.current;
  }
}
